import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Input,
  Paper,
  MenuItem,
  ToggleButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  Divider
} from '@mui/material';
import { MdCloudUpload } from 'react-icons/md';
import { FaEdit, FaEye } from 'react-icons/fa';
import MapPicker from '../MapPicker';
import { indianStates } from '../../Utils/states';

const SellerForm = ({
  sellerForm,
  handleFormChange,
  handleFileUpload,
  handleAddSeller,
  isEditing,
  setShowAddForm,
  hideTitle = false,
  hideBackButton = false,
  hideSubmitButton = false,
  sellerId = null,
  formMode = 'edit',
  handleFormModeChange = null
}) => {
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDocType, setSelectedDocType] = useState('');

  const steps = ['Business Information', 'Contact Persons', 'Documents', 'Store Info'];

  const docTypes = [
    { id: 'addressProof', label: 'Address Proof (Utility Bill/Rent Agreement)' },
    { id: 'gstProof', label: 'GST Registration Certificate' },
    { id: 'businessPanProof', label: 'Business PAN Card' },
    { id: 'shopActLicense', label: 'Shop & Establishment License' },
    { id: 'msmeUdyamCertificate', label: 'MSME / Udyam Certificate' },
    { id: 'cancelledCheque', label: 'Cancelled Cheque' },
  ];

  // Validate individual field
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) error = 'Name is required';
        break;
      case 'email':
        if (!value.trim()) error = 'Email is required';
        else if (!/^\S+@\S+\.\S+$/.test(value)) error = 'Invalid email format';
        break;
      case 'password':
        if (!isEditing && !value) error = 'Password is required';
        else if (!isEditing && value.length < 6) error = 'Password must be at least 6 characters';
        break;
      case 'mobile':
        if (!value.trim()) error = 'Mobile number is required';
        else if (!/^\d{10}$/.test(value.replace(/\D/g, ''))) error = 'Enter valid 10-digit mobile number';
        break;
      case 'business_name':
        if (!value.trim()) error = 'Business name is required';
        break;
      case 'businessAddress':
        if (!value.trim()) error = 'Business address is required';
        break;
      case 'businessCity':
        if (!value.trim()) error = 'City is required';
        break;
      case 'businessState':
        if (!value.trim()) error = 'State is required';
        break;
      case 'businessPincode':
        if (!value.trim()) error = 'Pincode is required';
        else if (!/^\d{6}$/.test(value)) error = 'Pincode must be 6 digits';
        break;
      case 'gstNumber':
        if (value.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value))
          error = 'Invalid GST format';
        break;
      case 'businessPAN':
        if (value.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) error = 'Invalid PAN format';
        break;
      case 'contactPerson2Email':
        if (value.trim() && !/^\S+@\S+\.\S+$/.test(value)) error = 'Invalid email format';
        break;
      case 'contactPerson2Mobile':
        if (value.trim() && !/^\d{10}$/.test(value.replace(/\D/g, ''))) error = 'Enter valid 10-digit mobile number';
        break;
      case 'contactPerson2Password':
        if (sellerForm.contactPerson2Name && !isEditing && !value) error = 'Password is required for access';
        else if (value && value.length < 6) error = 'Password must be at least 6 characters';
        break;
      case 'storeLatitude':
      case 'storeLongitude':
        if (!value) error = 'Please select location on map';
        break;
      default:
        break;
    }

    return error;
  };

  // Handle input change with live validation
  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'gstNumber' || name === 'businessPAN') {
      value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      e.target.value = value;
    }
    handleFormChange(e);

    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle file upload with validation
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (!file) {
      setErrors(prev => ({ ...prev, [name]: 'File is required' }));
      handleFileUpload(e);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      setErrors(prev => ({ ...prev, [name]: 'Only images & PDF allowed' }));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [name]: 'File size must be under 5MB' }));
      return;
    }

    setErrors(prev => ({ ...prev, [name]: undefined }));
    handleFileUpload(e);
  };

  // Final form validation before submit
  const validateForm = () => {
    const newErrors = {};

    const requiredFields = [
      'name', 'email', 'mobile', 'business_name', 'businessAddress',
      'businessCity', 'businessState', 'businessPincode'
    ];

    requiredFields.forEach(field => {
      if (!sellerForm[field]?.toString().trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    if (!isEditing && !sellerForm.password) newErrors.password = 'Password is required';

    // Second contact validation if name is entered
    if (sellerForm.contactPerson2Name) {
      if (!sellerForm.contactPerson2Email) newErrors.contactPerson2Email = 'Email required for secondary access';
      if (!sellerForm.contactPerson2Mobile) newErrors.contactPerson2Mobile = 'Mobile required for secondary access';
      if (!isEditing && !sellerForm.contactPerson2Password) newErrors.contactPerson2Password = 'Password required for secondary access';
    }

    if (!sellerForm.storeLatitude || !sellerForm.storeLongitude) {
      newErrors.storeLatitude = 'Please select store location on map';
    }

    // File validations (only for add mode)
    // File validations: Check if at least one document is uploaded (only for add mode)
    if (!isEditing) {
      const isAnyFileUploaded = docTypes.some(doc => !!sellerForm[doc.id]);
      if (!isAnyFileUploaded) {
        newErrors.documents = 'At least one document is required for registration';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const onSubmit = () => {
    setSubmitError('');
    if (!validateForm()) {
      setSubmitError('Please fix all errors before submitting.');
      // Find step with error
      const requiredStep0 = ['business_name', 'businessAddress', 'businessCity', 'businessState', 'businessPincode'];
      const requiredStep1 = ['name', 'email', 'mobile'];
      const requiredStep3 = ['storeLatitude', 'storeLongitude'];

      if (requiredStep0.some(f => !sellerForm[f]?.toString().trim())) setActiveStep(0);
      else if (requiredStep1.some(f => !sellerForm[f]?.toString().trim()) || (!isEditing && !sellerForm.password)) setActiveStep(1);
      else if (!isEditing && !docTypes.some(d => !!sellerForm[d.id])) setActiveStep(2);
      else if (requiredStep3.some(f => !sellerForm[f])) setActiveStep(3);

      return;
    }
    handleAddSeller();
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const renderViewField = (label, value) => (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography>{value || '-'}</Typography>
    </Box>
  );

  const fetchAddressFromLatLng = async (lat, lng) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY; // Using key from environment variables
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
      );
      const data = await response.json();
      
      if (data.status !== 'OK') {
        console.error('Google Geocoding error:', data.status);
        return null;
      }

      const result = data.results[0];
      const addressComponents = result.address_components;
      
      const getComponent = (type) => {
        const comp = addressComponents.find(c => c.types.includes(type));
        return comp ? comp.long_name : '';
      };

      return {
        storeAddress: result.formatted_address || '',
        storeArea: getComponent('sublocality_level_1') || getComponent('locality') || '',
        storeNearbyLandmark: getComponent('neighborhood') || '',
        storeCity: getComponent('locality') || getComponent('administrative_area_level_2') || '',
        storeState: getComponent('administrative_area_level_1') || '',
        storePincode: getComponent('postal_code') || '',
        storeLatitude: lat,
        storeLongitude: lng
      };
    } catch (error) {
      console.error('Error fetching address:', error);
      return {
        storeAddress: '', storeArea: '', storeNearbyLandmark: '',
        storeCity: '', storeState: '', storePincode: '', storeLatitude: lat, storeLongitude: lng
      };
    }
  };

  return (
    <>
      {sellerId && (
        <Box mb={2} p={2} sx={{ backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
          <Typography variant="body2" color="primary" fontWeight={600}>
            Registration Link ID: {sellerId}
          </Typography>
        </Box>
      )}

      <Box component={Paper} p={3} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
        {!hideTitle && (
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif' }}>
              {isEditing ? (formMode === 'edit' ? 'Edit Seller' : 'View Seller') : 'Add New Seller'}
            </Typography>
            <Box display="flex" gap={2}>
              {isEditing && handleFormModeChange && (
                <ToggleButton value={formMode} onClick={handleFormModeChange} size="small"
                  sx={{ background: 'rgba(0,0,0,0.08)', color: 'black' }}>
                  {formMode === 'view' ? <>Edit</> : <>View</>}
                </ToggleButton>
              )}
              {!hideBackButton && (
                <Button variant="contained" onClick={() => setShowAddForm(false)}
                  sx={{ background: 'red', color: 'white', '&:hover': { background: 'darkred' } }}>
                  Back
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <>
            <Typography mb={2} fontWeight={600} sx={{ fontFamily: 'Mulish, sans-serif' }}>Business Information</Typography>
            <Grid container spacing={3} mb={3}>
              {['business_name', 'gstNumber', 'businessPAN', 'businessAddress', 'businessCity', 'businessState', 'businessPincode'].map(field => (
                <Grid item xs={12} sm={6} md={field === 'businessAddress' ? 8 : 4} key={field}>
                  {formMode === 'view' ? renderViewField(
                    field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                    sellerForm[field]
                  ) : (
                    <TextField
                      fullWidth
                      select={field === 'businessState'}
                      label={`${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}${!['gstNumber', 'businessPAN'].includes(field) ? '' : ''}`}
                      name={field}
                      value={sellerForm[field] || ''}
                      onChange={handleInputChange}
                      required={!['gstNumber', 'businessPAN'].includes(field)}
                      error={!!errors[field]}
                      helperText={errors[field]}
                    >
                      {field === 'businessState' && indianStates.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Typography mb={2} fontWeight={600} sx={{ fontFamily: 'Mulish, sans-serif' }}>Business Contact Persons</Typography>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, mb: 3, bgcolor: '#fcfcfc' }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>Contact Person 1 (Primary)</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Name', sellerForm.name) : (
                    <TextField fullWidth label="Name" name="name" value={sellerForm.name || ''}
                      onChange={handleInputChange} required
                      error={!!errors.name} helperText={errors.name} />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Email', sellerForm.email) : (
                    <TextField fullWidth label="Email" name="email" type="email" value={sellerForm.email || ''}
                      onChange={handleInputChange} required
                      error={!!errors.email} helperText={errors.email} />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Mobile', sellerForm.mobile) : (
                    <TextField fullWidth label="Mobile" name="mobile" value={sellerForm.mobile || ''}
                      onChange={handleInputChange} required
                      error={!!errors.mobile} helperText={errors.mobile} />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Login Password', isEditing ? '••••••••' : 'Not set') : (
                    <TextField fullWidth label="Login Password" name="password" type="password" value={sellerForm.password || ''}
                      onChange={handleInputChange} required={!isEditing}
                      error={!!errors.password} helperText={errors.password || (isEditing ? "Leave blank to keep current" : "")} />
                  )}
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, mb: 3, bgcolor: '#fcfcfc' }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>Contact Person 2 (Secondary - Provides Login Access)</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Name', sellerForm.contactPerson2Name) : (
                    <TextField fullWidth label="Name" name="contactPerson2Name" value={sellerForm.contactPerson2Name || ''}
                      onChange={handleInputChange}
                      error={!!errors.contactPerson2Name} helperText={errors.contactPerson2Name} />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Email', sellerForm.contactPerson2Email) : (
                    <TextField fullWidth label="Email" name="contactPerson2Email" type="email" value={sellerForm.contactPerson2Email || ''}
                      onChange={handleInputChange}
                      error={!!errors.contactPerson2Email} helperText={errors.contactPerson2Email} />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Mobile', sellerForm.contactPerson2Mobile) : (
                    <TextField fullWidth label="Mobile" name="contactPerson2Mobile" value={sellerForm.contactPerson2Mobile || ''}
                      onChange={handleInputChange}
                      error={!!errors.contactPerson2Mobile} helperText={errors.contactPerson2Mobile} />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {formMode === 'view' ? renderViewField('Login Password', isEditing ? '••••••••' : 'Not set') : (
                    <TextField fullWidth label="Login Password" name="contactPerson2Password" type="password" value={sellerForm.contactPerson2Password || ''}
                      onChange={handleInputChange}
                      error={!!errors.contactPerson2Password} helperText={errors.contactPerson2Password} />
                  )}
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {activeStep === 2 && (
          <>
            <Typography mb={2} fontWeight={600} sx={{ fontFamily: 'Mulish, sans-serif' }}>Document Uploads</Typography>

            {errors.documents && (
              <Alert severity="error" sx={{ mb: 3 }}>{errors.documents}</Alert>
            )}

            {formMode === 'view' ? (
              <Grid container spacing={3} mb={3}>
                {docTypes.map(doc => (
                  <Grid item xs={12} sm={6} md={4} key={doc.id}>
                    {renderViewField(
                      doc.label,
                      (typeof sellerForm[doc.id] === 'string' && sellerForm[doc.id].startsWith('http')) ? (
                        <a href={sellerForm[doc.id]} target="_blank" rel="noopener noreferrer" style={{ color: '#2196f3', textDecoration: 'underline' }}>
                          View Document
                        </a>
                      ) : (sellerForm[doc.id]?.name || 'Not uploaded')
                    )}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box mb={3}>
                <Grid container spacing={3} alignItems="flex-end">
                  <Grid item xs={12} sm={6} md={5}>
                    <FormControl fullWidth>
                      <InputLabel>Select Document Type</InputLabel>
                      <Select
                        value={selectedDocType}
                        label="Select Document Type"
                        onChange={(e) => setSelectedDocType(e.target.value)}
                      >
                        {docTypes.map(doc => (
                          <MenuItem key={doc.id} value={doc.id}>{doc.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<MdCloudUpload />}
                      fullWidth
                      disabled={!selectedDocType}
                      sx={{
                        height: 56,
                        bgcolor: 'rgb(255 209 2)',
                        color: 'black',
                        fontWeight: 700,
                        '&:hover': { bgcolor: 'rgb(255 209 2)', opacity: 0.9 }
                      }}
                    >
                      Upload {selectedDocType ? docTypes.find(d => d.id === selectedDocType).label : 'File'}
                      <Input type="file" name={selectedDocType} onChange={handleFileChange} style={{ display: 'none' }} />
                    </Button>
                  </Grid>
                </Grid>

                <Box mt={4}>
                  <Typography variant="subtitle2" gutterBottom>Uploaded Documents Status:</Typography>
                  <Grid container spacing={2}>
                    {docTypes.map(doc => (
                      <Grid item xs={12} sm={6} md={4} key={doc.id}>
                        <Box sx={{
                          p: 1.5,
                          border: '1px solid',
                          borderColor: sellerForm[doc.id] ? 'success.light' : '#eee',
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          bgcolor: sellerForm[doc.id] ? '#f6fff6' : 'transparent'
                        }}>
                          <Typography variant="body2">{doc.label}</Typography>
                          {sellerForm[doc.id] ? (
                            <Typography variant="caption" color="success.main" fontWeight={700}>Uploaded</Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">Optional</Typography>
                          )}
                        </Box>
                        {errors[doc.id] && <Typography color="error" variant="caption">{errors[doc.id]}</Typography>}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            )}
          </>
        )}

        {activeStep === 3 && (
          <>
            {/* Store Information */}
            <Typography mb={2} fontWeight={600} sx={{ fontFamily: 'Mulish, sans-serif' }}>Store Information</Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12}>
                {formMode === 'view' ? (
                  <Box>
                    <Typography variant="subtitle2" mb={1}>Store Location</Typography>
                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9' }}>
                      <Typography>Lat: {sellerForm.storeLatitude || 'Not set'}</Typography>
                      <Typography>Lng: {sellerForm.storeLongitude || 'Not set'}</Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography variant="subtitle2" mb={1}>Select Store Location on Map *</Typography>
                    {errors.storeLatitude && <Alert severity="error" sx={{ mb: 2 }}>{errors.storeLatitude}</Alert>}
                    <MapPicker
                      lat={sellerForm.storeLatitude}
                      lng={sellerForm.storeLongitude}
                      onChange={async ({ lat, lng }) => {
                        const addr = await fetchAddressFromLatLng(lat, lng);
                        if (addr) {
                          Object.entries(addr).forEach(([name, value]) => {
                            handleFormChange({ target: { name, value } });
                          });
                          setErrors(prev => ({ ...prev, storeLatitude: undefined }));
                        }
                      }}
                    />
                  </>
                )}
                <Grid container spacing={2} mt={2}>
                  {['storeAddress', 'storeArea', 'storeNearbyLandmark', 'storeCity', 'storeState', 'storePincode'].map(field => (
                    <Grid item xs={12} sm={6} md={4} key={field}>
                      {formMode === 'view' ? renderViewField(
                        field.replace('store', '').replace(/([A-Z])/g, ' $1').trim(),
                        sellerForm[field]
                      ) : (
                        <TextField
                          fullWidth
                          select={field === 'storeState'}
                          label={`${field.replace('store', '').replace(/([A-Z])/g, ' $1').trim()}${['storeAddress', 'storeCity', 'storeState', 'storePincode'].includes(field) ? '' : ''}`}
                          name={field}
                          value={sellerForm[field] || ''}
                          onChange={handleFormChange}
                          multiline={field === 'storeAddress' && field !== 'storeState'}
                          rows={field === 'storeAddress' ? 2 : 1}
                          required={['storeAddress', 'storeCity', 'storeState', 'storePincode'].includes(field)}
                        >
                          {field === 'storeState' && indianStates.map((state) => (
                            <MenuItem key={state} value={state}>
                              {state}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField fullWidth label="Latitude" value={sellerForm.storeLatitude || ''} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField fullWidth label="Longitude" value={sellerForm.storeLongitude || ''} InputProps={{ readOnly: true }} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Status */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={6} md={4}>
                {formMode === 'view' ? renderViewField('Status', sellerForm.status) : (
                  <TextField select fullWidth label="Status" name="status" value={sellerForm.status || 'pending'} onChange={handleFormChange}>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </TextField>
                )}
              </Grid>
            </Grid>
          </>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
            sx={{ px: 4, py: 1, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Back
          </Button>
          <Box>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  background: 'rgb(255 209 2)', color: 'black', fontWeight: 700, px: 5, py: 1, borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': { background: 'rgb(255 209 2)', opacity: 0.9 }
                }}
              >
                Next
              </Button>
            ) : (
              !hideSubmitButton && formMode === 'edit' && (
                <Button variant="contained" onClick={onSubmit}
                  sx={{
                    background: 'rgb(255 209 2)', color: 'black', border: '1px solid black',
                    fontWeight: 700, px: 5, py: 1, borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': { background: 'rgb(255 209 2)' }
                  }}>
                  {isEditing ? 'Update Seller' : 'Submit Seller'}
                </Button>
              )
            )}
          </Box>
        </Box>

        {/* Bottom Error */}
        {submitError && <Alert severity="error" sx={{ mt: 3 }}>{submitError}</Alert>}
      </Box>
    </>
  );
};

export default SellerForm;